import React, { useState, useEffect } from 'react';

interface InstallPromptProps {
  currentUserId?: string;
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ currentUserId }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user has chosen "don't ask again" (user-specific)
    const dontAskAgain = currentUserId 
      ? localStorage.getItem(`raedar_dont_ask_install_${currentUserId}`)
      : localStorage.getItem('raedar_dont_ask_install'); // fallback for non-logged-in users
    
    if (dontAskAgain === 'true') {
      return;
    }

    // Listen for install prompt
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Show prompt even if browser doesn't trigger beforeinstallprompt
    // (for iOS Safari and other browsers)
    const timer = setTimeout(() => {
      if (!dontAskAgain) {
        setShowPrompt(true);
      }
    }, 3000); // Show after 3 seconds

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [currentUserId]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // For browsers that don't support beforeinstallprompt (like iOS)
      // Show instructions
      alert('To install rAEdar:\n\n📱 iPhone: Tap the share button → "Add to Home Screen"\n🤖 Android: Tap "Install" in the browser menu\n💻 Desktop: Look for the install button in your browser');
    }
  };

  const handleNotNow = () => {
    setShowPrompt(false);
  };

  const handleDontAskAgain = () => {
    // Save user-specific preference
    if (currentUserId) {
      localStorage.setItem(`raedar_dont_ask_install_${currentUserId}`, 'true');
    } else {
      localStorage.setItem('raedar_dont_ask_install', 'true');
    }
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      right: '20px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      zIndex: 1000,
      maxWidth: '400px',
      margin: '0 auto',
      animation: 'slideUp 0.3s ease-out'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <img 
          src="/raedar_icon.png" 
          alt="rAEdar" 
          style={{ width: '48px', height: '48px', borderRadius: '12px' }}
        />
        <div>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            Install rAEdar App
          </h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            Add to your home screen for quick access
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>⚡</span>
          <span style={{ fontSize: '14px', color: '#374151' }}>Faster access</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px' }}>📱</span>
          <span style={{ fontSize: '14px', color: '#374151' }}>Full-screen experience</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🔒</span>
          <span style={{ fontSize: '14px', color: '#374151' }}>Secure install (still requires internet)</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleDontAskAgain}
          style={{
            flex: 1,
            background: 'transparent',
            border: '1px solid #d1d5db',
            color: '#6b7280',
            padding: '10px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          Don't Ask Again
        </button>
        <button
          onClick={handleNotNow}
          style={{
            flex: 1,
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            color: '#374151',
            padding: '10px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
        >
          Not Now
        </button>
        <button
          onClick={handleInstall}
          style={{
            flex: 1,
            background: '#667eea',
            border: 'none',
            color: 'white',
            padding: '10px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a67d8'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
        >
          Install
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default InstallPrompt;
